import React, { useMemo, useState } from 'react';
import { Building2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const numberValue = (...values) => {
  const value = values.find(item => item !== undefined && item !== null && item !== '');
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const organizationValue = (row, keys) => {
  const key = keys.find(item => row?.[item] !== undefined && row?.[item] !== null);
  return key ? row[key] : '';
};

export const OrgSummaryTable = ({ data = [], isLoading = false }) => {
  const navigate = useNavigate();
  const [type, setType] = useState('Corporation');
  const [search, setSearch] = useState('');

  const organizations = useMemo(() => {
    const grouped = new Map();
    (Array.isArray(data) ? data : []).forEach(row => {
      const code = String(organizationValue(row, ['org_id', 'org_code', 'OrgCode', 'org', 'organization_code']) || '').trim();
      const name = String(organizationValue(row, ['org_name', 'OrgName', 'organization_name']) || code || 'Unknown Organization').trim();
      const key = (code || name).toLowerCase();
      const current = grouped.get(key) || { code, name, assets: 0, completed: 0, today: 0, yesterday: 0 };
      current.assets += numberValue(row.total, row.total_assets, row.asset_count, row.assets);
      current.completed += numberValue(row.completed, row.completed_count, row.total_completed);
      current.today += numberValue(row.today, row.today_completed);
      current.yesterday += numberValue(row.yesterday, row.yesterday_completed);
      grouped.set(key, current);
    });
    return [...grouped.values()].map(item => ({
      ...item,
      pending: Math.max(0, item.assets - item.completed),
      type: /corporation/i.test(item.name) ? 'Corporation' : 'Municipality'
    }));
  }, [data]);

  const visible = organizations.filter(item => item.type === type &&
    `${item.name} ${item.code}`.toLowerCase().includes(search.trim().toLowerCase()));

  const openCompleted = (organization, period) => {
    navigate(`/organization-assets/${encodeURIComponent(organization.code || organization.name)}`, {
      state: { orgCode: organization.code, orgName: organization.name, period, status: 'completed' }
    });
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Building2 className="h-5 w-5 text-blue-600" />Organization Performance</h2>
          <p className="mt-1 text-sm text-gray-500">Assets and completed survey status by organization</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex rounded-lg bg-gray-100 p-1">
            {['Corporation', 'Municipality'].map(item => <button key={item} onClick={() => setType(item)} className={`rounded-md px-4 py-2 text-sm font-medium ${type === item ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>{item}</button>)}
          </div>
          <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search organization" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm sm:w-64" /></div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500"><tr><th className="px-5 py-3">Organization</th><th className="px-4 py-3 text-right">Assets</th><th className="px-4 py-3 text-right">Completed Today</th><th className="px-4 py-3 text-right">Completed Yesterday</th><th className="px-4 py-3 text-right">All Completed</th><th className="px-5 py-3 text-right">Pending</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? <tr><td colSpan="6" className="px-5 py-12 text-center text-gray-500">Loading organization data...</td></tr> : visible.length === 0 ? <tr><td colSpan="6" className="px-5 py-12 text-center text-gray-500">No {type.toLowerCase()} data found.</td></tr> : visible.map(item => (
              <tr key={item.code || item.name} className="hover:bg-gray-50">
                <td className="px-5 py-4"><div className="font-semibold text-gray-900">{item.name}</div>{item.code && <div className="mt-1 text-xs text-gray-500">{item.code}</div>}</td>
                <td className="px-4 py-4 text-right font-semibold">{item.assets.toLocaleString('en-IN')}</td>
                <td className="px-4 py-4 text-right"><button onClick={() => openCompleted(item, 'today')} className="font-semibold text-blue-600 hover:underline">{item.today.toLocaleString('en-IN')}</button></td>
                <td className="px-4 py-4 text-right"><button onClick={() => openCompleted(item, 'yesterday')} className="font-semibold text-blue-600 hover:underline">{item.yesterday.toLocaleString('en-IN')}</button></td>
                <td className="px-4 py-4 text-right"><button onClick={() => openCompleted(item, 'all')} className="font-semibold text-green-600 hover:underline">{item.completed.toLocaleString('en-IN')}</button></td>
                <td className="px-5 py-4 text-right font-semibold text-amber-600">{item.pending.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
